import React, { JSX, useContext, useEffect, useMemo, useRef, useState, } from 'react';
import { Text, RichText, Field, LinkField, Link, ImageField, Image, } from '@sitecore-jss/sitecore-jss-nextjs';
import { ItemValue } from 'components/types/ListItem';
import ThemeContext from 'components/context/ThemeContext';
import AOS from 'aos';
import Icons from '../atoms/Icons';
import { classNames } from 'assets/utils/helper';
import { getMarginSize } from 'assets/utils/getMarginSize';
import { StyleguideComponentProps } from 'lib/component-props';

type SpotlightCarouselChild = {
  fields: {
    MarginSize: {
      fields: {
        heading: {
          value: string;
        };
      };
    };
    BaseHeadline: Field<string>;
    BaseDescription: Field<string>;
    BaseAlignment: ItemValue;
    BaseColor: ItemValue;
    BaseLink: LinkField;
    BaseLinkType: ItemValue;
    DesktopImage: ImageField;
    MobileImage?: ImageField;
    BaseGradient: { value: boolean };
    Parallax: { value: boolean };
    BasePhoneNumber: Field<string>;
  };
};

type SpotlightCarouselParentProps = StyleguideComponentProps & {
  fields: {
    MarginSize: {
      fields: {
        heading: {
          value: string;
        };
      };
    };
    Title: Field<string>;
    SpotlightCarouselChildList: SpotlightCarouselChild[];
  };
  rendering: { uid: string };
};

type SlideProps = {
  fields: SpotlightCarouselChild['fields'];
  isActive: boolean;
  theme?: string;
  totalSlides: number;
  activeIndex: number;
  onSelectSlide: (index: number) => void;
  progress: number;
  showTimer: boolean;
};

//Child Slide Component
const SpotLightCarouselSlide = ({
  fields,
  isActive,
  theme,
  totalSlides,
  activeIndex,
  onSelectSlide,
  progress,
  showTimer,
}: SlideProps): JSX.Element => {
  const elementRef = useRef<HTMLDivElement | null>(null);
  const [isLgUp, setIsLgUp] = useState(false);

  // Track viewport size to choose desktop vs mobile image
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const mq = window.matchMedia('(min-width: 1024px)');
    const update = () => setIsLgUp(mq.matches);
    update();
    mq.addEventListener?.('change', update);
    return () => mq.removeEventListener?.('change', update);
  }, []);

  useEffect(() => {
    if (!isActive) return;
    AOS?.refresh?.();
  }, [isActive, theme]);

  // Pick the correct image mobile or desktop
  const chosenImage: ImageField | undefined = useMemo(() => {
    const desktop = fields.DesktopImage?.value?.src
      ? fields.DesktopImage
      : undefined;
    const mobile = fields.MobileImage?.value?.src
      ? fields.MobileImage
      : undefined;
    if (desktop && mobile) return isLgUp ? desktop : mobile;
    return desktop ?? mobile;
  }, [fields.DesktopImage, fields.MobileImage, isLgUp]);

  const hasImage = Boolean(chosenImage?.value?.src);
  const safeAlt = chosenImage?.value?.alt ?? fields?.BaseHeadline?.value ?? '';

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (!isActive) return;
    if (!(theme === 'sfcon' || theme === 'sacon')) return;
    if (!fields.Parallax?.value) return;

    const el = elementRef.current;
    if (!el || !el.classList.contains('parallax')) return;

    const getBgImg = () => {
      return el.querySelector(
        ':scope > img, :scope img'
      ) as HTMLImageElement | null;
    };

    let ticking = false;

    const handleScroll = () => {
      if (ticking) return;
      ticking = true;

      requestAnimationFrame(() => {
        ticking = false;

        const rect = el.getBoundingClientRect();
        if (rect.bottom <= 0 || rect.top >= window.innerHeight) return;

        const currImg = getBgImg();
        if (!currImg) return;

        const imgTransform =
          (-(currImg.height - rect.height) / 2) *
          ((window.innerHeight - (rect.top - rect.height / 2)) /
            window.innerHeight);

        currImg.style.transform = `translateY(${imgTransform}px)`;
      });
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();

    return () => window.removeEventListener('scroll', handleScroll);
  }, [isActive, theme, fields.Parallax?.value]);

  const linkType = fields.BaseLinkType?.name?.toLowerCase() ?? '';
  const showDots = totalSlides >= 2 && totalSlides <= 4;
  const phoneNumber = fields.BasePhoneNumber?.value?.trim();
  const linkText = fields.BaseLink?.value?.text?.trim();
  const linkClassName = fields.BaseLinkType?.name
    ? `btn-mobile-fw btn-${fields.BaseLinkType.name.toLowerCase()}`
    : 'btn-mobile-fw';

  return (
    <div className="overhauled">
      <div
        ref={elementRef}
        className={`spotlight narrow inner-vertical-spacing ${
          hasImage
            ? 'image-bg'
            : fields.BaseGradient?.value
              ? 'gradient gradient-bg-dark-light'
              : 'bg-secondary'
        } ${
          isActive &&
          (theme === 'sfcon' || theme === 'sacon') &&
          fields.Parallax?.value
            ? 'parallax'
            : ''
        }`}
      >
        {hasImage && <Image field={chosenImage as ImageField} alt={safeAlt} />}

        {hasImage && (
          <div
            className={`overlay ${
              fields.BaseAlignment?.name
                ? fields.BaseAlignment.name.toLowerCase()
                : ''
            } ${fields.BaseColor?.name ? fields.BaseColor.name.toLowerCase() : ''} ${
              fields.BaseGradient?.value ? 'gradient' : ''
            }`}
          />
        )}

        <div
          className={`jss-inner-container content-wrapper ${
            fields.BaseAlignment?.name
              ? `align-${fields.BaseAlignment.name.toLowerCase()}`
              : ''
          }`}
        >
          {fields.BaseGradient?.value && !hasImage ? (
            <div className="text-wrapper">
              <Text tag="h2" className="headline" field={fields.BaseHeadline} />
              <RichText
                className="osf-intro-copy description"
                field={fields.BaseDescription}
              />
            </div>
          ) : (
            <>
              <Text
                tag="h2"
                className={hasImage ? 'osf-display-h2 headline' : 'headline'}
                field={fields.BaseHeadline}
              />
              <RichText
                className="osf-intro-copy description"
                field={fields.BaseDescription}
              />
            </>
          )}

          {fields?.BaseLinkType?.name &&
          (phoneNumber ? (
            <a
              href={`tel:${phoneNumber}`}
              className={linkClassName}
              data-aos={
                theme === 'sfcon' || theme === 'sacon'
                  ? 'fade-up-5'
                  : undefined
              }
              data-aos-delay={400}
              data-aos-easing="ease-in-out"
              data-aos-duration={1000}
            >
              {linkText || phoneNumber}
            </a>
          ) : (
            fields?.BaseLink?.value?.href &&
            linkType && (
              <Link
                field={fields?.BaseLink?.value}
                className={linkClassName}
                data-aos={
                  theme === 'sfcon' || theme === 'sacon'
                    ? 'fade-up-5'
                    : undefined
                }
                data-aos-delay={400}
                data-aos-easing="ease-in-out"
                data-aos-duration={1000}
              />
            )
          ))}

          <div className="dots">
            {showDots && (
              <div
                className="spotlight-carousel-dots"
                role="tablist"
                aria-label="Spotlight carousel slide selector"
              >
                {Array.from({ length: totalSlides }).map((_, i) => (
                  <button
                    key={`dot-${i}`}
                    type="button"
                    className={`spotlight-carousel-dot ${i === activeIndex ? 'is-active' : ''}`}
                    onClick={() => onSelectSlide(i)}
                    aria-label={`Go to slide ${i + 1}`}
                    aria-current={i === activeIndex ? 'true' : undefined}
                  />
                ))}
              </div>
            )}
          </div>
        </div>

        {showTimer && (
          <div className="spotlight-carousel-timer" aria-hidden="true">
            <div
              className="spotlight-carousel-timer-fill"
              style={{ transform: `scaleX(${progress})` }}
            />
          </div>
        )}
      </div>
    </div>
  );
};

//Parent Component
const SpotLightCarouselParent = (
  props: SpotlightCarouselParentProps
): JSX.Element => {
  const marginSize = getMarginSize(
    props?.fields?.MarginSize?.fields?.heading?.value
  );
  const { fields, rendering } = props;

  const slides = fields.SpotlightCarouselChildList ?? [];
  const total = slides.length;
  if (!total) return <></>;

  const theme = useContext(ThemeContext)?.themeState?.theme;

  const DURATION_MS = 12_000;
  const HOLD_MS = 250;
  const [activeIndex, setActiveIndex] = useState(0);
  const [progress, setProgress] = useState(0);
  const rafRef = useRef<number | null>(null);
  const startRef = useRef<number>(0);
  const pausedRef = useRef(false);
  const reducedMotionRef = useRef(false);
  const [isMdUp, setIsMdUp] = useState(false);
  const [isSwipeEnabled, setIsSwipeEnabled] = useState(false);

  const swipeStartX = useRef<number | null>(null);
  const swipeDeltaX = useRef(0);
  const SWIPE_THRESHOLD = 40;

  const [hasInteracted, setHasInteracted] = useState(false);
  const hasInteractedRef = useRef(false);

  const stop = () => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    rafRef.current = null;
  };

  const disableAutoplay = () => {
    if (hasInteractedRef.current) return;
    hasInteractedRef.current = true;
    setHasInteracted(true);
    setProgress(0);
    stop();
  };

  const goTo = (idx: number) => {
    disableAutoplay();
    setActiveIndex(idx);
  };
  const prev = () => {
    disableAutoplay();
    setActiveIndex(i => (i - 1 + total) % total);
  };
  const next = () => {
    disableAutoplay();
    setActiveIndex(i => (i + 1) % total);
  };

  useEffect(() => {
    if (typeof window === 'undefined') return;

    reducedMotionRef.current =
      window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches ?? false;

    const mqMd = window.matchMedia('(min-width: 768px)');
    const updateMd = () => setIsMdUp(mqMd.matches);
    updateMd();
    mqMd.addEventListener?.('change', updateMd);
    const mqSwipe = window.matchMedia('(max-width: 1023px)');
    const updateSwipe = () => setIsSwipeEnabled(mqSwipe.matches);
    updateSwipe();
    mqSwipe.addEventListener?.('change', updateSwipe);

    return () => {
      mqMd.removeEventListener?.('change', updateMd);
      mqSwipe.removeEventListener?.('change', updateSwipe);
    };
  }, []);

  useEffect(() => {
    if (!hasInteractedRef.current) setProgress(0);
    stop();

    if (typeof window === 'undefined') return;
    if (total <= 1) return;
    if (!isMdUp) return;
    if (reducedMotionRef.current) return;
    if (hasInteractedRef.current) return;

    pausedRef.current = false;
    startRef.current = performance.now();

    const tick = (now: number) => {
      const elapsed = now - startRef.current;
      const p = Math.min(1, elapsed / DURATION_MS);
      setProgress(p);

      if (p >= 1) {
        stop();
        window.setTimeout(() => {
          setActiveIndex(i => (i + 1) % total);
        }, HOLD_MS);
        return;
      }

      rafRef.current = requestAnimationFrame(tick);
    };

    rafRef.current = requestAnimationFrame(tick);
    return () => stop();
  }, [activeIndex, total, isMdUp]);

  const onMouseEnter = () => {
    if (reducedMotionRef.current) return;
    if (!isMdUp) return;
    if (hasInteractedRef.current) return;
    pausedRef.current = true;
    stop();
  };

  const onMouseLeave = () => {
    if (reducedMotionRef.current) return;
    if (!isMdUp) return;
    if (hasInteractedRef.current) return;

    pausedRef.current = false;
    startRef.current = performance.now() - progress * DURATION_MS;

    stop();
    rafRef.current = requestAnimationFrame(now => {
      const tick = (t: number) => {
        const elapsed = t - startRef.current;
        const p = Math.min(1, elapsed / DURATION_MS);
        setProgress(p);

        if (p >= 1) {
          stop();
          window.setTimeout(() => {
            setActiveIndex(i => (i + 1) % total);
          }, HOLD_MS);
          return;
        }
        rafRef.current = requestAnimationFrame(tick);
      };
      tick(now);
    });
  };

  const onSelectSlide = (i: number) => goTo(i);
  const onClickCapture = () => {
    disableAutoplay();
  };

  const onPointerDown = (e: React.PointerEvent) => {
    disableAutoplay();
    if (!isSwipeEnabled) return;
    swipeStartX.current = e.clientX;
    swipeDeltaX.current = 0;
  };

  const onPointerMove = (e: React.PointerEvent) => {
    if (!isSwipeEnabled) return;
    if (swipeStartX.current == null) return;
    swipeDeltaX.current = e.clientX - swipeStartX.current;
  };

  const onPointerUpOrCancel = () => {
    if (!isSwipeEnabled) return;
    if (swipeStartX.current == null) return;

    const dx = swipeDeltaX.current;

    swipeStartX.current = null;
    swipeDeltaX.current = 0;

    if (Math.abs(dx) < SWIPE_THRESHOLD) return;

    if (dx < 0) next();
    else prev();
  };

  return (
    <section
      className={classNames('overhauled spotlight-carousel', marginSize)}
      id={`spotlight-carousel-${rendering.uid}`}
      key={rendering.uid}
      data-theme={theme ?? ''}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      onClickCapture={onClickCapture}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUpOrCancel}
      onPointerCancel={onPointerUpOrCancel}
    >
      <div className="spotlight-carousel-track" aria-live="polite">
        <div
          className="spotlight-carousel-track-inner"
          style={{ transform: `translateX(-${activeIndex * 100}%)` }}
        >
          {slides.map((s, i) => (
            <div
              className="spotlight-carousel-slide"
              key={`${rendering.uid}-${i}`}
            >
              <SpotLightCarouselSlide
                fields={s.fields}
                isActive={i === activeIndex}
                theme={theme}
                totalSlides={total}
                activeIndex={activeIndex}
                onSelectSlide={onSelectSlide}
                progress={progress}
                showTimer={total > 1 && isMdUp && !hasInteracted}
              />
            </div>
          ))}
        </div>
      </div>

      <div className="spotlight-carousel-ui">
        <div className="jss-inner-container">
          <button
            type="button"
            className="spotlight-carousel-btn spotlight-carousel-btn--prev"
            onClick={prev}
            aria-label="Previous slide"
          >
            <Icons id="chevron-left-icon" className="ce-chevron" />
          </button>

          <button
            type="button"
            className="spotlight-carousel-btn spotlight-carousel-btn--next"
            onClick={next}
            aria-label="Next slide"
          >
            <Icons id="chevron-left-icon"className="ce-chevron" />
          </button>
        </div>
      </div>
    </section>
  );
};

export default SpotLightCarouselParent;
